import 'core-js/stable';
import 'regenerator-runtime/runtime';
import { BrowserRouter } from 'react-router-dom';
import React from 'react';
import { render, hydrate } from 'react-dom';
import PropTypes from 'prop-types';
import { ApolloProvider } from 'react-apollo';
import { ApolloLink, Observable } from 'apollo-link';
import createBrowserApolloClient from '@sm/apollo-clients/dist/browser';
import './entry.scss';

import { L10nProvider } from '@sm/intl';
import { getClientEnvironmentDetails } from '@sm/utils';
import { WrenchTheme } from '@wds/styles';
import {
  ErrorBoundary,
  FiveHundredErrorPage,
  HelmetProvider,
  StaticProvider,
  initializeClientErrorHandler,
  GlobalThemeProvider,
} from '@sm/webassets';
import getGraphQLFragmentMatcher from './helpers/fragmentMatcher';

import CoreWebApp from './App';

const target = document.getElementById('reactApp');

const allClientStaticData = getClientEnvironmentDetails().isBrowser ? window.SM.__LOAD_PAYLOAD_CACHE__ || {} : {};

/**
 * Our servers inject the payload with the `user` details and, rightfully so, leave out the authentication attribute.
 * Hence, the client/webs need to calculate whether the user is authenticated or not by looking at the userId in the webs.
 * Now, we don't want every app/page to do this; hence, we abstract the calculation out.
 * In addition, we do not want the attribute readily available in the `window` object; hence, we
 * calculate it here and directly add it to the context.
 * Yes, the user can still access it (debugging JS/React Dev Tools) but this way, it's not readily exposed.
 *
 * With this in place, the webs can retrieve the authentication status via the context.
 */
allClientStaticData.user = allClientStaticData.user || {};
allClientStaticData.user.isAuthenticated = allClientStaticData.user.id && allClientStaticData.user.id !== '1';

const {
  environment,
  pageRequestId,
  'client-config': { appName, appVersion, graphQLUri },
  gql: { gqlCache = {} } = {},
  'bugsnag-client-config': bugSnagClientConfig,
} = allClientStaticData;

// Setup error handler to be available across the web
initializeClientErrorHandler(bugSnagClientConfig);

const LocalizedApp = ({ children, localeCode, languageCode }) => {
  const [messages, setMessages] = React.useState(localeCode === 'en-US' ? {} : null);

  React.useEffect(() => {
    if (localeCode === 'en-US') {
      return;
    }
    Promise.all([
      import(/* webpackChunkName: "i18n/[request]" */ `../locales/translated/${localeCode}`),
      import(/* webpackChunkName: "i18n/webassets/[request]" */ `@sm/webassets/dist/locales/translated/${localeCode}`),
      import(
        /* webpackChunkName: "i18n/contentmodules/[request]" */ `@sm/contentmodules/dist/locales/translated/${localeCode}`
      ),
    ]).then(([appMessages, webassetsMessages, contentModulesMessages]) => {
      setMessages({
        ...appMessages,
        ...webassetsMessages,
        ...contentModulesMessages,
      });
    });
  }, [localeCode]);

  if (!messages) {
    return null;
  }
  return (
    <L10nProvider localeCode={localeCode} languageCode={languageCode} localeMessages={messages}>
      {children}
    </L10nProvider>
  );
};

LocalizedApp.propTypes = {
  children: PropTypes.node.isRequired,
  localeCode: PropTypes.string.isRequired,
  languageCode: PropTypes.string.isRequired,
};

const apolloClient = createBrowserApolloClient({
  uri: graphQLUri,
  cacheHydration: gqlCache,
  pageRequestId,
  fragmentMatcherFn: getGraphQLFragmentMatcher,
  linkOptions: {
    credentials: 'include',
    batchInterval: 30,
    batchKey: operation => {
      if (operation.getContext().batchName) {
        return operation.getContext().batchName;
      }
      return 'smweb';
    },
  },
  metadata: { appName, appVersion },
  availableLoggedOutPaths: [
    '/lo-graphql/crosstabs',
    '/lo-graphql/rir',
    '/lo-graphql/live-results',
    '/lo-graphql/trackingSharedDashboard',
  ],
  authLink: new ApolloLink((operation, forward) => {
    return new Observable(observer => {
      let sub;
      try {
        sub = forward(operation).subscribe({
          next: result => {
            if (result.errors) {
              const hasUnAuthenticatedError = result.errors.find(err => err.extensions.code === 'SM_UNAUTHENTICATED');
              if (hasUnAuthenticatedError) {
                // Triggering LOGOUT
                window.location.href = `/user/sign-in/?ep=${encodeURIComponent(window.location.pathname)}`;
              }
            }
            observer.next(result);
          },
          error: err => observer.error(err),
          complete: () => observer.complete(),
        });
      } catch (e) {
        observer.error(e);
      }
      return () => {
        if (sub) {
          sub.unsubscribe();
        }
      };
    });
  }),
});

const rendered = (
  <GlobalThemeProvider theme={WrenchTheme}>
    <ErrorBoundary FallbackComponent={FiveHundredErrorPage}>
      <HelmetProvider>
        <ApolloProvider client={apolloClient}>
          <BrowserRouter>
            <StaticProvider value={{ ...allClientStaticData }}>
              <LocalizedApp localeCode={environment.slLanguageLocale} languageCode={environment.languageCode}>
                <CoreWebApp />
              </LocalizedApp>
            </StaticProvider>
          </BrowserRouter>
        </ApolloProvider>
      </HelmetProvider>
    </ErrorBoundary>
  </GlobalThemeProvider>
);

(target.innerHTML.trim().length ? hydrate : render)(rendered, document.getElementById('reactApp'));
